import React, { useEffect, useRef, useState } from "react";
import { AiDisclaimer } from "../components/AiDisclaimer";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { LongevityMeter, TrafficLight } from "../components/Longevity";
import { BioAge } from "../components/BioAge";
import { InfoHint } from "../components/InfoHint";
import { VoiceButton } from "../components/VoiceButton";
import { NutritionPanel } from "../components/NutritionPanel";
import { ProtocolCard } from "../components/ProtocolCard";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Send, Utensils, Activity, Sparkles, ShieldAlert, HeartPulse, Salad } from "lucide-react";

const GL_COLORS = { basso: "#3B604E", medio: "#D4A017", alto: "#B7410E" };

export default function Diary() {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [entries, setEntries] = useState([]);
  const [longevity, setLongevity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signals, setSignals] = useState(null);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("practical");

  const protocolEnabled = !!user?.anti_inflammatory_enabled;

  const last7 = Array.from({ length: 8 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().slice(0, 10);
  });

  const load = () => {
    api.get("/diary").then((r) => setEntries(r.data)).catch(() => {});
    api.get("/longevity").then((r) => setLongevity(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/diary", { text, lang, date: entryDate, mode });
      setText("");
      setLongevity(res.data.longevity);
      setEntries((e) => [res.data.entry, ...e]);
      setSignals(res.data.signals || null);
      refreshUser();
      const earned = res.data.entry.points_earned;
      toast.success(`+${earned} ${t("points_earned")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const toggleProtocol = async (enabled) => {
    try {
      await api.patch("/protocol/anti-inflammatory", { enabled });
      await refreshUser();
      toast.success(enabled ? t("protocol_activated") : t("protocol_deactivated"));
      if (enabled) setSignals(null);
    } catch (e) {
      toast.error("Errore");
    }
  };

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-4 hover:text-[#3F6E85]" data-testid="diary-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{t("diary_title")}</h1>
          <InfoHint
            testid="diary-info"
            text={lang === "it"
              ? "Scrivi in modo naturale e completo. Per l'ALIMENTAZIONE indica anche i condimenti (es. 'pasta con zucchine condita con burro e olio EVO'). Per l'ATTIVITÀ specifica tipo, intensità e durata (es. '1 ora di cardio ad alta intensità con pesi'). Indica gli orari quando possibile: ciò che scrivi verrà aggiunto in automatico ai diari Alimentazione ed Esercizio."
              : "Write naturally and fully. For NUTRITION include dressings/condiments (e.g. 'pasta with zucchini dressed with butter and EVO oil'). For ACTIVITY specify type, intensity and duration (e.g. '1 hour of high-intensity cardio with weights'). Add times when possible: what you write is automatically added to your Nutrition and Exercise logs."}
          />
        </div>
        <p className="text-[#6E6B65] mb-8 max-w-xl">{t("diary_sub")}</p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="diary-mode-toggle">
          <span className="text-sm font-medium text-[#6E6B65]">{t("diary_analysis_style")}:</span>
          <div className="inline-flex rounded-full bg-[#F3F1ED] p-1">
            <button onClick={() => setMode("practical")} data-testid="diary-mode-practical"
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "practical" ? "bg-[#3F6E85] text-white shadow-sm" : "text-[#6E6B65]"}`}>
              {t("report_practical")}
            </button>
            <button onClick={() => setMode("scientific")} data-testid="diary-mode-scientific"
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "scientific" ? "bg-[#3F6E85] text-white shadow-sm" : "text-[#6E6B65]"}`}>
              {t("report_scientific")}
            </button>
          </div>
        </div>

        <NutritionPanel />

        {/* Anti-inflammatory protocol — manual toggle always visible + intelligent NLP prompt */}
        <div className="rounded-2xl p-4 mb-4 bg-white border border-stone-200 flex items-center gap-3" data-testid="protocol-manual-toggle">
          <Salad className="w-5 h-5 text-[#3B604E] shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#2B4A59]">{t("protocol_activate")}</p>
            <p className="text-[11px] text-[#6E6B65]">{t("protocol_disclaimer")}</p>
          </div>
          <Switch checked={protocolEnabled} onCheckedChange={(v) => toggleProtocol(v)} data-testid="protocol-switch" />
        </div>

        {protocolEnabled ? (
          <div className="mb-8">
            <ProtocolCard />
          </div>
        ) : signals && (signals.stress || signals.inflammation) ? (
          <div data-testid="protocol-prompt" className="rounded-2xl p-5 mb-8 bg-[#FBEEDF] border border-[#f0d3ad]">
            <div className="flex items-center gap-2 mb-1 text-[#B7410E] font-semibold">
              <ShieldAlert className="w-5 h-5" /> {t(signals.inflammation ? "protocol_inflammation_detected" : "protocol_stress_detected")}
            </div>
            <p className="text-sm text-[#7a5a2a] mb-3">{t("protocol_offer")}</p>
            <Button onClick={() => toggleProtocol(true)} data-testid="protocol-activate-btn" className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white gap-2">
              <Salad className="w-4 h-4" /> {t("protocol_activate")}
            </Button>
          </div>
        ) : null}

        <div className="mb-8">
          <BioAge />
        </div>

        {/* Natural language input */}
        <div className="rounded-[1.5rem] p-6 bg-white border border-stone-200 mb-8 shadow-sm">
          <Textarea
            data-testid="diary-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${Math.max(e.target.scrollHeight, 150)}px`; }}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder={t("diary_placeholder")}
            className="rounded-xl border-none focus-visible:ring-0 resize-none overflow-hidden min-h-[150px] text-base p-0 leading-relaxed"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-2">
              <VoiceButton onResult={(txt) => setText((prev) => (prev ? prev + " " + txt : txt))} />
              <label className="flex items-center gap-1 text-sm text-[#6E6B65]">
                {t("diary_date")}:
                <select
                  data-testid="diary-date-select"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  className="rounded-lg border border-stone-200 px-2 py-1 text-sm bg-white"
                >
                  {last7.map((d, i) => (
                    <option key={d} value={d}>{i === 0 ? t("today_label") : d}</option>
                  ))}
                </select>
              </label>
            </div>
            <Button data-testid="diary-send" onClick={send} disabled={busy} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] gap-2">
              <Send className="w-4 h-4" /> {busy ? t("analyzing") : t("diary_send")}
            </Button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="rounded-2xl p-10 bg-[#F3F1ED] text-center text-[#6E6B65]" data-testid="diary-empty">
            {t("diary_empty")}
          </div>
        ) : (
          <div className="space-y-5" data-testid="diary-list">
            {entries.map((e) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-white border border-stone-200 overflow-hidden" data-testid="diary-entry">
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <p className="text-[#2B4A59] font-medium italic">"{e.text}"</p>
                    <span className="shrink-0 rounded-full px-3 py-1 text-sm font-semibold bg-[#EAF0EC] text-[#3F6E85]">+{e.points_earned}</span>
                  </div>

                  {e.food && (
                    <div className="mt-4 rounded-xl bg-[#FBF9F4] p-5 border border-stone-100">
                      <div className="flex items-center gap-2 mb-3 text-[#3F6E85] font-semibold">
                        <Utensils className="w-4 h-4" /> {t("nutrition")}
                        <TrafficLight value={e.food.traffic_light} />
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm mb-3">
                        <Chip label={t("glycemic_load")} value={e.food.glycemic_load} color={GL_COLORS[e.food.glycemic_load]} />
                      </div>
                      {e.food.nutrients && (
                        <div className="grid sm:grid-cols-3 gap-3 text-sm mb-3">
                          <NutrientBox title={t("vitamins")} items={e.food.nutrients.vitamine} />
                          <NutrientBox title={t("antioxidants")} items={e.food.nutrients.antiossidanti} />
                          <div>
                            <p className="text-xs uppercase tracking-wider text-[#6E6B65] mb-1">{t("macros")}</p>
                            <p className="text-[#3a3a3a]">{e.food.nutrients.macros}</p>
                          </div>
                        </div>
                      )}
                      <Advice icon={Sparkles} label={t("advice")} text={e.food.advice} />
                    </div>
                  )}

                  {e.activity && (
                    <div className="mt-4 rounded-xl bg-[#EFF4F0] p-5 border border-stone-100">
                      <div className="flex items-center gap-2 mb-3 text-[#3F6E85] font-semibold">
                        <Activity className="w-4 h-4" /> {e.activity.name}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm mb-3">
                        <Meta label={t("intensity")} value={e.activity.intensity} />
                        <Meta label={t("duration")} value={`${e.activity.duration_min} min`} />
                        <Meta label={t("calories")} value={`${e.activity.calories_est} kcal`} />
                      </div>
                      <Advice icon={HeartPulse} label={t("recovery_meal")} text={e.activity.recovery_meal} />
                      <Advice icon={ShieldAlert} label={t("injury_prevention")} text={e.activity.injury_prevention} />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {entries.length > 0 && <AiDisclaimer />}
      </div>
    </div>
  );
}

const Chip = ({ label, value, color }) => (
  <span className="inline-flex items-center gap-2">
    <span className="text-xs uppercase tracking-wider text-[#6E6B65]">{label}:</span>
    <span className="rounded-full px-3 py-0.5 text-sm font-semibold capitalize" style={{ backgroundColor: (color || "#6E6B65") + "22", color: color || "#6E6B65" }}>{value}</span>
  </span>
);

const Meta = ({ label, value }) => (
  <span className="inline-flex flex-col">
    <span className="text-xs uppercase tracking-wider text-[#6E6B65]">{label}</span>
    <span className="font-semibold capitalize text-[#2B4A59]">{value}</span>
  </span>
);

const NutrientBox = ({ title, items }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-[#6E6B65] mb-1">{title}</p>
    <div className="flex flex-wrap gap-1">
      {(items || []).length ? items.map((it, i) => (
        <span key={i} className="rounded-md bg-white border border-stone-200 px-2 py-0.5 text-xs">{it}</span>
      )) : <span className="text-[#6E6B65] text-xs">—</span>}
    </div>
  </div>
);

const Advice = ({ icon: Icon, label, text }) => (
  text ? (
    <div className="mt-3 flex gap-3">
      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-[#B5651D]" />
      <div>
        <p className="text-xs uppercase tracking-wider text-[#6E6B65] mb-0.5">{label}</p>
        <p className="text-sm text-[#3a3a3a] leading-relaxed">{text}</p>
      </div>
    </div>
  ) : null
);
